import {
  IonButton,
  IonCheckbox,
  IonContent,
  IonLoading,
  IonModal,
  IonSelect,
  IonSelectOption
} from '@ionic/react';
import React, { useCallback, useEffect, useState } from 'react';

import {
  ButtonContainer,
  ModalHeader,
  Padding,
  Spacer
} from '@/components/common';
import { Icon } from '@/components/memo';
import { useDentist } from '@/contexts';
import { DentistHour } from '@/data/DentistHours';
import { useToast } from '@/hooks';
import { DAY_NAMES_ARRAY } from '@/utils/constants';
import { parseTime } from '@/utils/helpers/parseTime';
import { format, isAfter, isEqual } from 'date-fns';
import { arrowBackOutline, checkmarkCircleOutline } from 'ionicons/icons';

import './UpdateWorkingHours.css';

interface UpdateWorkingHoursProps {
  isOpen: boolean;
  onClose: () => void;
  presentingElement?: HTMLElement | undefined;
}

const UpdateWorkingHours: React.FC<UpdateWorkingHoursProps> = ({
  isOpen,
  onClose,
  presentingElement
}) => {
  const displayToast = useToast();
  const { dentistHours, updateHours } = useDentist();

  const [loading, setLoading] = useState<boolean>(false);

  const copy = {
    subHeader: 'WORKING HOURS',
    header: (
      <>
        Manage your <span>working</span> hours
      </>
    ),
    message:
      'Please check all the days you are working and specify the working hours for each day.'
  };

  const initialHours = Array.from({ length: 7 }, (_, day) => ({
    day,
    start_time: null,
    end_time: null,
    open: false
  }));

  const [hours, setHours] = useState<DentistHour[]>(initialHours);

  useEffect(() => {
    if (dentistHours.length > 0) {
      setHours((prevHours) =>
        prevHours.map((hour) => {
          const matchingHour = dentistHours.find((dh) => dh.day === hour.day);
          return matchingHour ? { ...hour, ...matchingHour } : hour;
        })
      );
    }
  }, [dentistHours]);

  const timeOptions = Array.from({ length: 48 }, (_, i) => {
    const hours = String(Math.floor((i + 8) / 2) % 24).padStart(2, '0');
    const minutes = (i + 12) % 2 === 0 ? '00' : '30';
    return `${hours}:${minutes}`;
  });

  const onTimeChange = (
    day: number,
    type: 'start_time' | 'end_time',
    value: string
  ) => {
    setHours((prevHours) =>
      prevHours.map((hour) =>
        hour.day === day || !hour.open ? { ...hour, [type]: value } : hour
      )
    );
  };

  const onCheckboxChange = (day: number, open: boolean) => {
    setHours((prevHours) =>
      prevHours.map((hour) => (hour.day === day ? { ...hour, open } : hour))
    );
  };

  const onNext = useCallback(async () => {
    const validHours = hours.filter(
      (hour) =>
        !hour.open ||
        (hour.start_time &&
          hour.end_time &&
          isAfter(parseTime(hour.end_time), parseTime(hour.start_time)))
    );
    if (validHours.length > 0) {
      try {
        setLoading(true);
        await updateHours(validHours);
        setLoading(false);
        onClose();
      } catch {
        setLoading(false);
        displayToast({
          message: 'Something went wrong. Please try again.',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
      }
    } else {
      displayToast({
        message: 'Please add at least one valid working day and specify hours.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  }, [hours, onClose]);

  useEffect(() => {
    return () => {
      setLoading(false);
    };
  }, []);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      presentingElement={presentingElement}
    >
      <IonContent>
        <Padding withSafeArea={!presentingElement}>
          <div className="update-working-hours">
            <ModalHeader
              leftIcon={arrowBackOutline}
              onClickLeftIcon={onClose}
            />
            <Spacer top={16} bottom={24}>
              <h1 className="update-working-hours__header">{copy.header}</h1>
              <p className="update-working-hours__message">{copy.message}</p>
            </Spacer>

            <Spacer bottom={124}>
              <div className="update-working-hours__working-days">
                {hours.map((hour) => (
                  <div
                    key={hour.day}
                    className="update-working-hours__working-day"
                  >
                    <IonCheckbox
                      className="update-working-hours__day"
                      checked={hour.open}
                      onIonChange={(e: CustomEvent) =>
                        onCheckboxChange(hour.day, e.detail.checked)
                      }
                    >
                      <p className="update-working-hours__day-label">
                        {DAY_NAMES_ARRAY[hour.day]}
                      </p>
                    </IonCheckbox>
                    {hour.open && (
                      <>
                        <Spacer top={12} />
                        <div className="update-working-hours__working-hours">
                          <div className="update-working-hours__input">
                            <IonSelect
                              label="Start Time"
                              labelPlacement="stacked"
                              value={hour.start_time}
                              compareWith={(a: string, b: string) =>
                                isEqual(parseTime(a), parseTime(b))
                              }
                              placeholder="Select a time"
                              onIonChange={(e: CustomEvent) =>
                                onTimeChange(
                                  hour.day,
                                  'start_time',
                                  e.detail.value
                                )
                              }
                            >
                              {timeOptions.map((time) => (
                                <IonSelectOption key={time} value={time}>
                                  {format(parseTime(time), 'h:mm a')}
                                </IonSelectOption>
                              ))}
                            </IonSelect>
                          </div>
                          <div className="update-working-hours__input">
                            <IonSelect
                              label="End Time"
                              labelPlacement="stacked"
                              value={hour.end_time}
                              compareWith={(a: string, b: string) =>
                                isEqual(parseTime(a), parseTime(b))
                              }
                              placeholder="Select a time"
                              onIonChange={(e: CustomEvent) =>
                                onTimeChange(
                                  hour.day,
                                  'end_time',
                                  e.detail.value
                                )
                              }
                            >
                              {timeOptions
                                .filter(
                                  (time) =>
                                    !hour.start_time ||
                                    isAfter(
                                      parseTime(time),
                                      parseTime(hour.start_time)
                                    )
                                )
                                .map((time) => (
                                  <IonSelectOption key={time} value={time}>
                                    {format(parseTime(time), 'h:mm a')}
                                  </IonSelectOption>
                                ))}
                            </IonSelect>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </Spacer>

            <ButtonContainer>
              <IonButton
                id="next-button"
                onClick={onNext}
                expand="block"
                fill="solid"
                size="default"
                color="primary"
                strong={true}
                disabled={loading}
              >
                <Icon slot="start" icon={checkmarkCircleOutline} />
                Update
              </IonButton>
            </ButtonContainer>
          </div>
        </Padding>
        <IonLoading isOpen={loading} message="Loading..." spinner="crescent" />
      </IonContent>
    </IonModal>
  );
};

export default UpdateWorkingHours;
